import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function RegisterPhotographer() {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "", company_name: "", email: "", password: "",
    phone: "", website: "", sample_url: "",
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/register/photographer", form);
      if (data.token) localStorage.setItem("auth_token", data.token);
      await refresh();
      toast.success("Application submitted!");
      navigate("/studio", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-6 pt-16 pb-24" data-testid="register-photographer-page">
      <div className="pm-slash mb-6 text-sm"><span>Photographer Application</span></div>
      <h1 className="font-display text-4xl md:text-6xl uppercase leading-[0.95] tracking-tight">
        Apply to <span className="pm-orange">join</span>
      </h1>
      <p className="text-white/60 font-condensed mt-4">
        Applications reviewed within 48 hours. You can sign in and set up your watermark straight away, but you cannot publish photos until approved.
      </p>

      <form onSubmit={submit} className="mt-10 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="text-xs uppercase tracking-[0.3em] pm-orange block mb-1 font-semibold">Your name</label>
            <input required value={form.name} onChange={set("name")} className="input-minimal" data-testid="reg-name" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.3em] pm-orange block mb-1 font-semibold">Company / brand name</label>
            <input required value={form.company_name} onChange={set("company_name")} placeholder="e.g. Berm Media" className="input-minimal" data-testid="reg-company" />
            <p className="text-xs text-white/40 mt-1 font-condensed">Used on your photos as the watermark by default.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="text-xs uppercase tracking-[0.3em] pm-orange block mb-1 font-semibold">Email</label>
            <input required type="email" value={form.email} onChange={set("email")} className="input-minimal" data-testid="reg-email" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.3em] pm-orange block mb-1 font-semibold">Password</label>
            <input required type="password" minLength={6} value={form.password} onChange={set("password")} className="input-minimal" data-testid="reg-password" />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="text-xs uppercase tracking-[0.3em] text-white/40 block mb-1 font-semibold">Phone (optional)</label>
            <input value={form.phone} onChange={set("phone")} className="input-minimal" data-testid="reg-phone" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.3em] text-white/40 block mb-1 font-semibold">Website / Instagram (optional)</label>
            <input value={form.website} onChange={set("website")} placeholder="e.g. instagram.com/bermmedia" className="input-minimal" data-testid="reg-website" />
          </div>
        </div>

        <div>
          <label className="text-xs uppercase tracking-[0.3em] text-white/40 block mb-1 font-semibold">Link to sample photos (optional)</label>
          <input value={form.sample_url} onChange={set("sample_url")} placeholder="Dropbox / Google Drive / portfolio URL" className="input-minimal" data-testid="reg-sample" />
        </div>

        {error && <p className="text-red-400 text-sm" data-testid="reg-error">{error}</p>}

        <button type="submit" disabled={busy} className="pm-btn" data-testid="reg-submit">
          {busy ? "Submitting…" : "Submit application"}
        </button>

        <p className="text-sm text-white/50 font-condensed">
          Already a photographer? <Link to="/login" className="pm-orange underline-offset-4 hover:underline">Sign in</Link>
        </p>
      </form>
    </div>
  );
}
